import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { getConfig } from '@local/app-config';
import { trackError } from '@local/metrics';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import { skipToken } from '@reduxjs/toolkit/query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContext, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { v4 as uuid } from 'uuid';
import { useLoadBentleyLicenseOrgsQuery } from '../../apiClients/entitlementsClient/requests/BentleyEntitlementsAPI';
import { useLazyRefreshBentleyAccessTokenQuery, useLazyRetrieveBentleyAccessTokenQuery, } from '../../apiClients/identityClient/requests/BentleyAccessTokenApi';
import { BENTLEY_SESSION_EXPIRED, EVO_APPS, EVO_NEXT_SERVICES, LOGIN_ERROR_ROUTE, LOGIN_ROUTE, LOGIN_SILENT, LOGOUT_ROUTE, LOGOUT_SUCCESS_ROUTE, } from '../../constants';
import { clearCombinedToken as clearAccessToken, getCombinedToken as getAccessToken, } from '../../store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import { clearCSRFState, getCSRFState } from '../../store/sessionStorageHelpers/authStateHelpers';
import { setDeeplink } from '../../store/sessionStorageHelpers/deeplinkHelper/deeplinkHelper';
import { clearEntitlements } from '../../store/sessionStorageHelpers/entitlementsHelper/entitlementsHelper';
import { clearPkceVerifier } from '../../store/sessionStorageHelpers/pkceFlowHelpers';
import { LOGIN_SIGNIN, LOGOUT, LOGIN_RETRY, SESSION_HAS_EXPIRED_TEXT, SESSION_HAS_EXPIRED_TITLE, SESSION_HAS_EXPIRED_RETRY_TEXT, } from '../../strings';
import { getBentleyAuthUrl } from '../../util/getAuthUrl';
import { LoginContext } from '../loginHandler/LoginContext';
function accessTokenInvalid(error) {
    if (error) {
        const { status } = error;
        if (status && typeof status === 'number' && status >= 400) {
            return true;
        }
    }
    return false;
}
function accessTokenUnauthorized(error) {
    if (error) {
        const { status } = error;
        if (status && typeof status === 'number' && status === 401) {
            return true;
        }
    }
    return false;
}
const HOUR_IN_MILLI = 3600000;
const SILENT_AUTH_POLL_TIME = 60000 * 25;
function WrappedIframe({ additionalScopes = [], tabId, }) {
    const { forceOauthPrefix } = useContext(LoginContext);
    const [silentAuthUrl, setSilentAuthUrl] = useState();
    const bentleyIdentityBaseUrl = getConfig().bentleyBaseUrl;
    const [iframeKey, setIframeKey] = useState(uuid());
    useEffect(() => {
        function getSilentAuthUrl() {
            return __awaiter(this, void 0, void 0, function* () {
                const url = yield getBentleyAuthUrl({
                    forceOauthPrefix,
                    additionalScopes,
                    tabId,
                    silent: true,
                });
                setSilentAuthUrl(url);
            });
        }
        if (!silentAuthUrl) {
            getSilentAuthUrl();
        }
        const timeoutId = setInterval(() => {
            setIframeKey(uuid());
        }, 60000 * 2);
        return () => {
            clearInterval(timeoutId);
        };
    }, []);
    if (!bentleyIdentityBaseUrl || !silentAuthUrl) {
        return null;
    }
    return (_jsx("iframe", { "automation-id": "bentley-silent-auth-iframe", "aria-hidden": "true", id: "BentleyAuthorize", title: "BentleyAuthorize", style: {
            position: 'absolute',
            opacity: '0',
            width: '1px',
            height: '1px',
            top: '0',
            left: '0',
            border: 'none',
            display: 'block',
            zIndex: '-1',
            pointerEvents: 'none',
            visibility: 'hidden',
        }, height: 0, width: 0, tabIndex: -1, src: silentAuthUrl.toString() }, iframeKey));
}
function isAccessTokenExpired() {
    const token = getAccessToken();
    const expiry = token === null || token === void 0 ? void 0 : token.expires_at;
    return expiry ? expiry <= Date.now() : true;
}
export function BentleyHiddenIframe({ children, additionalScopes = [], dialogEnabled = true, }) {
    const navigate = useNavigate();
    const { forceOauthPrefix } = useContext(LoginContext);
    const [loadIframe, setLoadIframe] = useState(false);
    const [expiredToken, setExpiredToken] = useState(false);
    const [tabIdentifier] = useState(uuid());
    const [open, setOpen] = useState(false);
    const [hasClickedSignIn, setHasClickedSignIn] = useState(false);
    const handleOpen = () => {
        setHasClickedSignIn(false);
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };
    const [getNewAccessToken, getNewAccessTokenResult] = useLazyRetrieveBentleyAccessTokenQuery();
    const token = getAccessToken();
    const expiry = token === null || token === void 0 ? void 0 : token.expires_at;
    const expired = expiry ? expiry <= Date.now() : true;
    function iframeEventHandler(event) {
        if (event.origin !== window.origin) {
            return;
        }
        if (event.data.source === 'bentley-silent-auth') {
            setLoadIframe(false);
            if (event.data.error) {
                if (isAccessTokenExpired()) {
                    if (!(location.pathname.startsWith(LOGIN_ROUTE) ||
                        location.pathname.startsWith(LOGOUT_ROUTE))) {
                        const deepLink = `${location.pathname}${location.search}`;
                        setDeeplink(deepLink);
                    }
                    navigate(`${LOGIN_ROUTE}?error=${BENTLEY_SESSION_EXPIRED}`);
                }
                else {
                    handleOpen();
                }
            }
            else {
                if (event.data.state !== getCSRFState(tabIdentifier)) {
                    if (!(location.pathname.startsWith(LOGIN_ROUTE) ||
                        location.pathname.startsWith(LOGOUT_ROUTE))) {
                        const deepLink = `${location.pathname}${location.search}`;
                        setDeeplink(deepLink);
                    }
                    trackError('Error CSRF states do not match', 'CSRF states do not match after silent auth attempt, potential CSRF attack', {
                        request: getCSRFState(tabIdentifier).substring(0, 7),
                        response: event.data.state.substring(0, 7),
                        tabIdentifier: tabIdentifier.substring(0, 7),
                    });
                    navigate(`${LOGIN_ERROR_ROUTE}?error=invalid_auth_state&bentley_token=true`);
                    return;
                }
                getNewAccessToken({
                    authCode: event.data.code,
                    forceOauthPrefix,
                    tabId: tabIdentifier,
                });
            }
        }
    }
    useEffect(() => {
        if (expired) {
            setLoadIframe(true);
            setExpiredToken(true);
        }
        else if (expiry) {
            const lifeInMilli = expiry - Date.now();
            const firstRefresh = HOUR_IN_MILLI - SILENT_AUTH_POLL_TIME;
            if (lifeInMilli < firstRefresh) {
                setLoadIframe(true);
            }
        }
        const timeoutId = setInterval(() => {
            setLoadIframe(true);
        }, SILENT_AUTH_POLL_TIME);
        window.addEventListener('message', iframeEventHandler);
        return () => {
            window.removeEventListener('message', iframeEventHandler);
            clearInterval(timeoutId);
            clearCSRFState(tabIdentifier);
            clearPkceVerifier(tabIdentifier);
        };
    }, []);
    useEffect(() => {
        const expiredTimeoutId = setInterval(() => {
            if (!expiredToken) {
                if (isAccessTokenExpired()) {
                    setLoadIframe(true);
                    setExpiredToken(true);
                }
            }
        }, 1000);
        return () => {
            clearInterval(expiredTimeoutId);
        };
    }, [expiredToken]);
    useEffect(() => {
        if (getNewAccessTokenResult.isError) {
            trackError('Error on silent auth', 'Could not get token from silent auth', getNewAccessTokenResult.error);
        }
        if (getNewAccessTokenResult.data) {
            setExpiredToken(false);
            handleClose();
        }
    }, [getNewAccessTokenResult]);
    function makeBroadcastChannel() {
        const state = getCSRFState();
        const bc = new BroadcastChannel(`extend_login.${state}`);
        bc.onmessage = (event) => {
            if (event.data.source === 'bentley-login-extension') {
                if (event.data.error) {
                    const deepLink = `${location.pathname}${location.search}`;
                    if (!(location.pathname.startsWith(LOGIN_ROUTE) ||
                        location.pathname.startsWith(LOGOUT_ROUTE))) {
                        setDeeplink(deepLink);
                    }
                    navigate(`${LOGIN_ERROR_ROUTE}?error=error_extending_session&bentley_token=true`);
                }
                else {
                    getNewAccessToken({
                        authCode: event.data.code,
                        forceOauthPrefix,
                        extendSession: true,
                    });
                    bc.close();
                }
            }
        };
    }
    const signInCallback = () => __awaiter(this, void 0, void 0, function* () {
        setHasClickedSignIn(true);
        const url = yield getBentleyAuthUrl({
            forceOauthPrefix,
            additionalScopes,
            extendSession: true,
        });
        makeBroadcastChannel();
        const width = 600;
        const height = 600;
        const y = window.outerHeight / 2 + window.screenY - height / 2;
        const x = window.outerWidth / 2 + window.screenX - width / 2;
        const newWindow = window.open(url, '_blank', `noopener,noreferrer,popup,width=${width},height=${height},left=${x},top=${y}`);
        if (newWindow)
            newWindow.opener = null;
    });
    const childrenAndDialog = () => {
        if (dialogEnabled) {
            if (expiredToken || expired) {
                return (_jsx(Stack, { sx: { height: '100vh', width: '100vw' }, justifyContent: "center", alignItems: "center", children: _jsx(CircularProgress, { "automation-id": "access-token-renewal-progress" }) }));
            }
            return (_jsxs(_Fragment, { children: [_jsxs(Dialog, { fullWidth: true, maxWidth: "xs", open: open, "automation-id": "silent-auth-session-expired-dialog", children: [_jsx(DialogTitle, { children: SESSION_HAS_EXPIRED_TITLE }), hasClickedSignIn ? (_jsxs(_Fragment, { children: [_jsx(DialogContent, { sx: {
                                            display: 'flex',
                                            justifyContent: 'center',
                                        }, children: _jsxs(Stack, { direction: "column", children: [_jsx(DialogContentText, { children: SESSION_HAS_EXPIRED_RETRY_TEXT }), _jsx(Box, { sx: { display: 'flex', justifyContent: 'center' }, children: _jsx(CircularProgress, { "automation-id": "expired-session-renewal-progress" }) })] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { variant: "outlined", color: "secondary", onClick: () => {
                                                    handleClose();
                                                    navigate(`${LOGOUT_ROUTE}`);
                                                }, children: LOGOUT }), _jsx(Button, { variant: "contained", onClick: signInCallback, children: LOGIN_RETRY })] })] })) : (_jsxs(_Fragment, { children: [_jsx(DialogContent, { children: _jsx(DialogContentText, { children: SESSION_HAS_EXPIRED_TEXT }) }), _jsxs(DialogActions, { children: [_jsx(Button, { variant: "outlined", color: "secondary", onClick: () => {
                                                    handleClose();
                                                    navigate(`${LOGOUT_ROUTE}`);
                                                }, children: LOGOUT }), _jsx(Button, { variant: "contained", onClick: signInCallback, children: LOGIN_SIGNIN })] })] }))] }), children] }));
        }
        return children;
    };
    return (_jsxs(_Fragment, { children: [loadIframe && (_jsx(WrappedIframe, { additionalScopes: additionalScopes, tabId: tabIdentifier })), childrenAndDialog()] }));
}
function clearSession() {
    clearAccessToken();
    clearCSRFState();
    clearEntitlements();
    clearPkceVerifier();
}
export function BentleyAccessTokenGuard({ children, additionalScopes = [], }) {
    var _a;
    const location = useLocation();
    const navigate = useNavigate();
    const flags = useFlags();
    const combineResponses = (_a = flags.evouiCombineDevIntgInstanc) !== null && _a !== void 0 ? _a : false;
    const [searchParams] = useSearchParams();
    const { forceOauthPrefix, service } = useContext(LoginContext);
    const [refreshToken, refreshTokenResult] = useLazyRefreshBentleyAccessTokenQuery();
    const authCode = searchParams.get('code');
    const errorCode = searchParams.get('error');
    const token = getAccessToken();
    const accessToken = token === null || token === void 0 ? void 0 : token.access_token;
    const expiry = token === null || token === void 0 ? void 0 : token.expires_at;
    const expired = expiry ? expiry <= Date.now() : true;
    const skipDiscovery = !accessToken ||
        (expired && !(token === null || token === void 0 ? void 0 : token.refresh_token)) ||
        service === 'none' ||
        service === 'central' ||
        flags.evouiCombineDevIntgInstanc === undefined;
    const { error } = useLoadBentleyLicenseOrgsQuery(skipDiscovery
        ? skipToken
        : Object.assign({ service: service === 'evo-next' ? EVO_NEXT_SERVICES : service }, (combineResponses && EVO_APPS.includes(service) && { combineResponses })));
    const noAccessToken = !accessToken && !authCode;
    useEffect(() => {
        if (accessTokenUnauthorized(error) && (token === null || token === void 0 ? void 0 : token.refresh_token)) {
            refreshToken({
                forceOauthPrefix,
                refreshToken: token.refresh_token,
            });
        }
        else if (accessTokenInvalid(error)) {
            clearSession();
            navigate(LOGIN_ROUTE);
        }
    }, [error]);
    const notLoginRoute = location.pathname !== LOGIN_ROUTE &&
        location.pathname !== LOGIN_ERROR_ROUTE &&
        location.pathname !== LOGOUT_SUCCESS_ROUTE &&
        location.pathname !== LOGIN_SILENT;
    if ((noAccessToken || (refreshTokenResult === null || refreshTokenResult === void 0 ? void 0 : refreshTokenResult.isError)) && notLoginRoute) {
        clearSession();
        const regex = 'login';
        if (errorCode) {
            searchParams.delete('error');
            searchParams.delete('error_description');
            const searchString = searchParams.toString();
            const deepLink = `${location.pathname}${searchString.length > 0 ? `?${searchString}` : ''}`;
            if (!regex.match(location.pathname)) {
                setDeeplink(deepLink);
            }
        }
        else if (!regex.match(location.pathname)) {
            setDeeplink(`${location.pathname}${location.search}`);
        }
        return _jsx(Navigate, { to: LOGIN_ROUTE, state: { from: location }, replace: true });
    }
    return (_jsx(BentleyHiddenIframe, { additionalScopes: additionalScopes, children: children }));
}
//# sourceMappingURL=BentleyAccessTokenGuard.js.map